import { useCheckPermission } from "@/hook/use-check-permission"
import { useLetaky } from "@/hook/useLetaky"
import { useLanguage } from "@/languages/LanguageProvider"
import { checkTitleLetaky, deleteLetaky, fetchLetaky } from "@/services/letaky"
import { useDomainStore } from "@/stores/use-domain-store"
import { Letak } from "@/types/codegen/data-contracts"
import { LetakyData, MediaData, Pagination } from "@/types/types"
import { getEnforcedDomainValue } from "@/utils/admin-domain-filter"
import { App, TablePaginationConfig } from "antd"
import { usePathname, useRouter, useSearchParams } from "next/navigation"
import queryString from "query-string"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { DEFAULT_LOCALE, DEFAULT_PAGINATION, SORT_PAGINATION_PARAMS } from "../constants"

type SingleImage = NonNullable<NonNullable<Letak["catalogImages"]>["data"]>[number]
type SetMedia = React.Dispatch<React.SetStateAction<MediaData[]>>

// Types
interface FilterState {
  sortPaginationValues: { [key: string]: string } | null
  filterValues: { [key: string]: string } | null
  defaultFilterValues: { [key: string]: any } | null
  apiLocale: string
  isInitialized: boolean
}

interface UseLetakyManagerReturn {
  // Data
  letaky: LetakyData[]
  loading: boolean
  error: string | null
  pagination: Pagination

  // Filter state
  filterState: FilterState
  currentLocale: string

  // Actions
  handleSearch: (filters: object) => void
  handleRefresh: (filterRef?: React.RefObject<{ filterFunction: () => void } | null>) => void
  handleClearFilters: () => void
  handleLocaleClear: () => void
  handleTableChange: (pagination: TablePaginationConfig, filters?: any, sorter?: any) => void
  handleDuplicate: (id: number) => Promise<void>
  handleDelete: (id: number) => Promise<void>
  hasPermission: (action: string) => boolean

  // Utils
  updateApiLocale: (locale: string) => void

  featuredImageValue: MediaData[]
  setFeaturedImageValue: SetMedia
  catalogImagesValue: MediaData[]
  setCatalogImagesValue: SetMedia
  pdfFileValue: MediaData[]
  setPdfFileValue: SetMedia
}

export const useLetakyManager = (
  options?: {
    listPopulate?: Record<string, unknown>
    pageSize?: number
    sortField?: string
    sortOrder?: "asc" | "desc"
  },
): UseLetakyManagerReturn => {
  const viewPageSize = options?.pageSize
  const viewSortField = options?.sortField
  const viewSortOrder = options?.sortOrder
  const searchParams = useSearchParams()
  // Populate the visible columns need (from Configure-the-view); kept in a ref
  // so the fetch callback stays stable and reads the latest value.
  const listPopulateRef = useRef(options?.listPopulate)
  listPopulateRef.current = options?.listPopulate
  const { language } = useLanguage()
  const router = useRouter()
  const pathname = usePathname()
  const { selectedDomain } = useDomainStore()
  const enforcedDomain = getEnforcedDomainValue(selectedDomain)
  const enforcedDomainRef = useRef(enforcedDomain)
  const { message } = App.useApp();

  // Memoized current locale
  const currentLocale = useMemo(() => language || DEFAULT_LOCALE, [language])

  // Data state
  const [letaky, setLetaky] = useState<LetakyData[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [pagination, setPagination] = useState<Pagination>(
    viewPageSize ? { ...DEFAULT_PAGINATION, pageSize: viewPageSize } : DEFAULT_PAGINATION,
  )
  // Guards: reqSeq drops stale responses, inFlightRequestKeyRef collapses the
  // React 19 StrictMode double-invoke into one request.
  const reqSeq = useRef(0)
  const inFlightRequestKeyRef = useRef<string | null>(null)
  // pageSize is owned by "Configure view" — reset to page 1 when it (or the sort)
  // changes. No "sync page size into pagination" effect any more (that fed the
  // double fetch — RC1); the fetch reads viewPageSize directly.
  useEffect(() => {
    if (!viewPageSize) return
    setPagination(prev =>
      prev.page === 1 && prev.current === 1 ? prev : { ...prev, page: 1, current: 1 },
    )
  }, [viewPageSize, viewSortField, viewSortOrder])
  const { fetchLetakyById, createLetaky } = useLetaky()
  // Filter state
  const [filterState, setFilterState] = useState<FilterState>({
    sortPaginationValues: null,
    filterValues: null,
    defaultFilterValues: { locale: "all" },
    apiLocale: "all",
    isInitialized: false,
  })

  const [featuredImageValue, setFeaturedImageValue] = useState<MediaData[]>([])
  const [catalogImagesValue, setCatalogImagesValue] = useState<MediaData[]>([])
  const [pdfFileValue, setPdfFileValue] = useState<MediaData[]>([])

  // Refs to prevent unnecessary re-renders
  const initializationRef = useRef(false)
  const lastParseKeyRef = useRef<string>("")
  const prevEnforcedDomainRef = useRef<string | undefined>(undefined)
  const ignoreDomainFromUrlOnceRef = useRef(false)

  useEffect(() => {
    enforcedDomainRef.current = enforcedDomain
  }, [enforcedDomain])

  // When the global domain switches from a specific site to "All websites", drop `domain` from the URL.
  // Otherwise the search-param string is unchanged, the parse effect bails out, and the list never refetches.
  useEffect(() => {
    if (!filterState.isInitialized) return

    const prev = prevEnforcedDomainRef.current
    prevEnforcedDomainRef.current = enforcedDomain

    if (prev && !enforcedDomain) {
      ignoreDomainFromUrlOnceRef.current = true
      const sp = new URLSearchParams(searchParams.toString())
      if (sp.has("domain")) {
        sp.delete("domain")
        const q = sp.toString()
        router.replace(q ? `${pathname}?${q}` : pathname)
      }
    }
  }, [enforcedDomain, filterState.isInitialized, pathname, router, searchParams])

  // Initialize only once
  useEffect(() => {
    if (!initializationRef.current) {
      const parsedParams = queryString.parse(searchParams.toString())
      const urlLocale = parsedParams.locale as string
      const targetLocale = urlLocale || "all"

      setFilterState(prev => ({
        ...prev,
        apiLocale: targetLocale,
        defaultFilterValues: { locale: targetLocale },
        isInitialized: true,
      }))
      initializationRef.current = true
    }
  }, [])

  // Parse URL when the query string or enforced header domain changes (both affect effective filters).
  useEffect(() => {
    if (!filterState.isInitialized) return

    const parseKey = `${searchParams.toString()}\0${enforcedDomain}`
    if (parseKey === lastParseKeyRef.current) return

    lastParseKeyRef.current = parseKey

    const parsedParams = queryString.parse(searchParams.toString())
    const newFilterValues: { [key: string]: string } = {}
    const newSortPaginationValues: { [key: string]: string } = {}
    let newApiLocale = filterState.apiLocale

    Object.entries(parsedParams).forEach(([key, value]) => {
      if (typeof value !== "string") return

      if (isSortPaginationParam(key, value)) {
        newSortPaginationValues[key] = value
      } else if (key === "locale") {
        newApiLocale = value
      } else {
        newFilterValues[key] = value
      }
    })

    if (enforcedDomain) {
      newFilterValues.domain = enforcedDomain
    } else if (ignoreDomainFromUrlOnceRef.current) {
      delete newFilterValues.domain
      ignoreDomainFromUrlOnceRef.current = false
    }

    setFilterState(prev => ({
      ...prev,
      sortPaginationValues: newSortPaginationValues,
      filterValues: newFilterValues,
      defaultFilterValues: { ...newFilterValues, locale: newApiLocale },
      apiLocale: newApiLocale,
    }))
  }, [searchParams, filterState.isInitialized, filterState.apiLocale, enforcedDomain])

  // Optimized fetch function
  const fetchLetakyData = useCallback(
    async (searchParams: { [key: string]: string } = {}, publicationState: string = "preview") => {
      const params: { [key: string]: any } = {
        ...searchParams,
        ...(enforcedDomainRef.current ? { domain: enforcedDomainRef.current } : {}),
        ...(listPopulateRef.current ? { populate: listPopulateRef.current } : {}),
      }

      // Set locale if not provided
      if (!params.locale) {
        params.locale = filterState.apiLocale
      }

      // Use current pagination state if not provided
      if (!params.page) {
        params.page = String(pagination.page)
      }
      if (!params.pageSize) {
        // pageSize is owned by "Configure view" — never read back from response.
        params.pageSize = String(viewPageSize ?? pagination.pageSize)
      }

      const requestKey = JSON.stringify({ params, publicationState })
      if (inFlightRequestKeyRef.current === requestKey) {
        return
      }
      inFlightRequestKeyRef.current = requestKey
      const seq = ++reqSeq.current

      try {
        setLoading(true)
        setError(null)
        const response = await fetchLetaky(params, publicationState)
        if (seq !== reqSeq.current) return // superseded by a newer request

        if (response?.error) {
          setError(response.error.message)
          return response
        }

        setLetaky(response?.data?.length > 0 ? response.data : [])
        // Only total/current — pageSize stays owned by viewConfig (RC2).
        setPagination(prev => ({
          ...prev,
          total: response?.meta?.pagination?.total ?? 0,
          current: response?.meta?.pagination?.page ?? 1,
        }))

        return response
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : "Unknown error"
        setError(errorMessage)
        message.error("Lấy letaky thất bại!")
        throw err
      } finally {
        if (inFlightRequestKeyRef.current === requestKey) {
          inFlightRequestKeyRef.current = null
        }
        if (seq === reqSeq.current) setLoading(false)
      }
    },
    [filterState.apiLocale, pagination.page, viewPageSize],
  )

  // Fetch data when filters change (optimized)
  useEffect(() => {
    if (!filterState.isInitialized) return

    const params: { [key: string]: string } = {
      locale: filterState.apiLocale,
      ...filterState.filterValues,
      ...filterState.sortPaginationValues,
    }

    // Apply the "Configure view" default sort when the URL specifies none.
    if (!params.sort && viewSortField) {
      params.sort = `${viewSortField}:${viewSortOrder ?? "desc"}`
    }

    fetchLetakyData(params, "preview")
  }, [
    filterState.filterValues,
    filterState.sortPaginationValues,
    filterState.apiLocale,
    fetchLetakyData,
    options?.listPopulate,
    viewSortField,
    viewSortOrder,
  ])

  // Optimized handlers
  const updateApiLocale = useCallback((locale: string) => {
    setFilterState(prev => ({ ...prev, apiLocale: locale }))
  }, [])

  const handleSearch = useCallback(
    (filters: object) => {
      const filterObj = filters as { [key: string]: string }
      const normalizedFilters: Record<string, string> = {
        ...filterObj,
        ...(enforcedDomain ? { domain: enforcedDomain } : {}),
      }

      if (normalizedFilters.locale) {
        updateApiLocale(normalizedFilters.locale)
      }

      const { locale, ...otherFilters } = normalizedFilters
      setFilterState(prev => ({
        ...prev,
        filterValues: otherFilters,
        sortPaginationValues: { ...prev.sortPaginationValues, page: "1" },
      }))

      const params = new URLSearchParams()
      Object.entries(normalizedFilters).forEach(([key, value]) => {
        if (value && value !== "all") {
          params.append(key, value)
        }
      })

      if (normalizedFilters.locale === "all") {
        params.delete("locale")
      }

      router.push(`${pathname}?${params.toString()}`)
    },
    [updateApiLocale, router, pathname, enforcedDomain],
  )

  const isTitleUnique = async (title: string): Promise<boolean> => {
    try {
      const response = await checkTitleLetaky(title)

      return response?.data?.length === 0
    } catch (error) {
      console.error("Lỗi kiểm tra tiêu đề đã tồn tại", error)
      return false
    }
  }

  const generateUniqueTitle = async (baseTitle: string): Promise<{ title: string }> => {
    const title = String(baseTitle || "")
    const cleanTitle = title.replace(/-copy-(\d+)$/, "")
    let count = 1
    let newTitle = `${cleanTitle}-copy-${count}`
    while (!(await isTitleUnique(newTitle))) {
      count++
      newTitle = `${cleanTitle}-copy-${count}`
    }

    return { title: newTitle }
  }

  const handleRefresh = useCallback(
    (filterRef?: { current: { filterFunction: () => void } | null }) => {
      if (filterRef?.current) {
        filterRef.current.filterFunction()
      }

      if (enforcedDomain) {
        const query = queryString.stringify({ domain: enforcedDomain })
        router.replace(query ? `${pathname}?${query}` : pathname)
        return
      }

      router.replace(pathname)
    },
    [pathname, router, enforcedDomain],
  )

  const handleClearFilters = useCallback(() => {
    setFilterState(prev => ({
      ...prev,
      filterValues: enforcedDomain ? { domain: enforcedDomain } : null,
      sortPaginationValues: { page: "1" },
      apiLocale: "all",
      defaultFilterValues: {
        locale: "all",
        ...(enforcedDomain ? { domain: enforcedDomain } : {}),
      },
    }))
  }, [currentLocale, enforcedDomain])

  const handleLocaleClear = useCallback(() => {
    setFilterState(prev => ({
      ...prev,
      apiLocale: "all",
      defaultFilterValues: { ...prev.defaultFilterValues, locale: "all" },
    }))
  }, [currentLocale])

  const handleTableChange = useCallback(
    (paginationConfig: TablePaginationConfig, filters?: any, sorter?: any) => {
      const newPagination = {
        current: paginationConfig.current || 1,
        pageSize: paginationConfig.pageSize || 20,
        total: paginationConfig.total || 0,
      }

      setPagination(prev => ({
        ...prev,
        ...newPagination,
      }))

      const params: { [key: string]: string } = {
        page: String(newPagination.current),
        pageSize: String(newPagination.pageSize),
      }

      // Handle sorting
      if (sorter && !Array.isArray(sorter) && sorter.field && sorter.order) {
        const field = Array.isArray(sorter.field) ? sorter.field[1] : sorter.field
        const order = sorter.order === "ascend" ? "asc" : "desc"
        params.sort = `${field}:${order}`
      }

      fetchLetakyData(params)
      window.scrollTo({ top: 0, behavior: "smooth" })
    },
    [fetchLetakyData],
  )

  const handleDuplicate = useCallback(
    async (id: number) => {
      try {
        setLoading(true)
        const response = await fetchLetakyById(id)
        const originalData = response?.attributes
        if (!originalData) {
          message.error("Dữ liệu Letaky không tồn tại")
          return
        }
        const oldtitle = originalData?.title
        const { title: newName } = await generateUniqueTitle(oldtitle)
        const duplicatePayload = {
          data: {
            title: newName,
            description: originalData.description,
            dateStart: originalData.dateStart,
            dateEnd: originalData.dateEnd,
            locale: originalData.locale,
            featuredImage: originalData.featuredImage?.data?.id,
            pdfFile: originalData.pdfFile?.data?.id,
            catalogImages: originalData.catalogImages?.data?.map((img: SingleImage) => img?.id),
            publishedAt: null,
          },
        }

        const createRes = await createLetaky(duplicatePayload)
        if (createRes) {
          message.success("Nhân bản Letaky thành công")
          await handleRefresh()
        }
      } catch (err) {
        console.error("Lỗi nhân bản", err)
        message.error("Thất bại nhân bản Letaky")
      } finally {
        setLoading(false)
      }
    },
    [handleRefresh, createLetaky, fetchLetakyById],
  )

  const handleDelete = useCallback(
    async (id: number) => {
      try {
        setLoading(true)
        await deleteLetaky(id)

        await fetchLetakyData()
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : "Unknown error"
        setError(errorMessage)
        message.error("Xóa letaky thất bại!")
      } finally {
        setLoading(false)
      }
    },
    [fetchLetakyData],
  )

  const { hasPermission: checkPermission } = useCheckPermission()
  const hasPermission = useCallback(
    (action: string) => {
      return checkPermission("letak", action)
    },
    [checkPermission],
  )

  return {
    // Data
    letaky,
    loading,
    error,
    pagination,

    // Filter state
    filterState,
    currentLocale,

    // Actions
    handleSearch,
    handleRefresh,
    handleClearFilters,
    handleLocaleClear,
    handleTableChange,
    handleDuplicate,
    handleDelete,
    hasPermission,

    // Utils
    updateApiLocale,

    featuredImageValue,
    setFeaturedImageValue,
    catalogImagesValue,
    setCatalogImagesValue,
    pdfFileValue,
    setPdfFileValue,
  }
}

// Helper functions
const isSortPaginationParam = (key: string, value: string): boolean => {
  return SORT_PAGINATION_PARAMS.includes(key as any) || SORT_PAGINATION_PARAMS.includes(value as any)
}
